<template>
  <WLBaseExpandable
    :is-disabled="!isCollapsible"
    class="wl-section-header bg-default"
    :initial-state="initialState"
    @is-open="handleExpandableOpenChange"
  >
    <template #summary="{ toggle }">
      <header
        ref="headerRef"
        class="flex items-center gap-1 overflow-hidden border-t border-solid border-t-theme-transparent p-4"
        :class="{ 'is-open': localIsOpen }"
        @click="isCollapsible && toggle()"
      >
        <div
          v-if="$slots.prefix"
          ref="prefixRef"
          class="flex items-center text-emphasis"
        >
          <slot name="prefix" />
        </div>
        <div class="flex select-none flex-col gap-y-0.5 overflow-hidden">
          <p
            ref="titleRef"
            class="wl-section-header__title truncate text-sm font-medium leading-5 text-emphasis"
          >
            {{ title }}
          </p>
          <p
            v-if="subtitle"
            ref="subtitleRef"
            class="wl-section-header__subtitle text-sm text-muted"
          >
            {{ subtitle }}
          </p>
        </div>
        <div v-if="$slots.suffix">
          <slot name="suffix" />
        </div>
        <div class="ml-auto flex gap-2">
          <div v-if="$slots.end" ref="endRef">
            <slot name="end" />
          </div>
          <button
            v-if="isCollapsible"
            class="focus-visible:outline-solid rounded focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
            :aria-expanded="localIsOpen"
            :aria-label="localIsOpen ? 'Collapse section' : 'Expand section'"
            @click.stop="toggle"
            @keydown.enter="toggle"
            @keydown.space.prevent="toggle"
            type="button"
          >
            <WLIcon
              class="outline-none transition"
              :class="{ 'rotate-180': localIsOpen }"
              :has-hover="false"
              :name="IconName.NAVIGATION_CHEVRON_DOWN"
            />
          </button>
        </div>
      </header>
    </template>
    <template #details>
      <div ref="bodyRef" class="wl-section-header__body px-4 pb-4">
        <slot />
      </div>
    </template>
  </WLBaseExpandable>
</template>
<script setup lang="ts">
import { IconName, WLIcon } from "../icon";
import { ref, toRefs } from "vue";
import { useWebHaptics } from "../../composables/use-web-haptics";
import type { SectionHeaderProps, SectionHeaderEmits } from "./types";
import { WLBaseExpandable } from "../base-expandable";

const props = withDefaults(defineProps<SectionHeaderProps>(), {
  isCollapsible: true,
  initialState: () => ({
    isOpen: true,
  }),
});

defineEmits<SectionHeaderEmits>();

const { title, subtitle, isCollapsible, initialState } = toRefs(props);

const localIsOpen = ref(initialState.value.isOpen);
const { triggerHaptic } = useWebHaptics();

const handleExpandableOpenChange = (isOpen: boolean): void => {
  localIsOpen.value = isOpen;
  triggerHaptic("soft");
};
</script>
